import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Menu from '../menu/Menu';
import RTTable from '../common/Table';
import RTSpinner from '../common/RTSpinner';
import tenantStorage from '../../utils/tenantStorage';
import RTButton from '../common/RTButton';
import { apiGet } from '../../api/apiClient';
import { toastError } from '../common/toast';
import '../common/rtCommon.css';
import './ViewPGList.css';

const ViewPGList = () => {
  const [loading, setLoading] = useState(false);
  const [pgList, setPgList] = useState([]);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ status: 'Active' });
  const [appliedFilters, setAppliedFilters] = useState({ status: 'Active' }); // Track actually applied filters
  const [isFilterCollapsed, setIsFilterCollapsed] = useState(false);
  const navigate = useNavigate();

  const fetchPGList = async (appliedFilters = filters) => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (appliedFilters.status) params.status = appliedFilters.status;
      if (appliedFilters.mobile) params.mobile = appliedFilters.mobile;
      if (appliedFilters.name) params.name = appliedFilters.name;
      
      console.log('Applied filters:', appliedFilters);
      console.log('API params:', params);
      
      const token = tenantStorage.getItem('token');
      const response = await apiGet('/paying-guest', { 
        params: params,
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setPgList(response.data || []);
      if (!response.data || response.data.length === 0) {
        toastError('No paying guests found for the selected filter.');
      }
    } catch (err) {
      setError('Failed to fetch paying guest list');
      toastError('Failed to fetch paying guest list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPGList({ status: 'Active' });
    // eslint-disable-next-line
  }, []);

  const handleFilterApply = (appliedFilters) => {
    setFilters(appliedFilters);
    setAppliedFilters(appliedFilters); // Update applied filters state
    fetchPGList(appliedFilters);
  };

  // Generate dynamic title based on actually applied filters (not form state)
  const getTableTitle = () => {
    let title = "Paying Guests";
    
    if (appliedFilters.status) {
      title += ` - ${appliedFilters.status}`;
    }
    
    if (appliedFilters.name) {
      title += ` (Name: "${appliedFilters.name}")`;
    }
    
    if (appliedFilters.mobile) {
      title += ` (Mobile: "${appliedFilters.mobile}")`;
    }
    
    return title;
  };

  const formatAllocationStatus = (status) => {
    if (!status) return 'N/A';
    switch (status) {
      case 'FutureAllocation':
        return 'Future Allocation';
      case 'Allocated':
        return 'Allocated';
      case 'AllocationEnded':
        return 'Allocation Ended';
      default:
        return status;
    }
  };

  const columns = [
    { Header: 'Name', accessor: 'name' },
    { Header: 'Mobile', accessor: 'mobile' },
    { Header: 'Status', accessor: 'status' },
    { 
      Header: 'Allocated Building', 
      accessor: 'building_name',
      Cell: ({ value }) => value || 'N/A'
    },
    { 
      Header: 'Room Details', 
      accessor: 'room_details',
      Cell: ({ value }) => value || 'N/A'
    },
    { 
      Header: 'Allocation Status', 
      accessor: 'allocation_status',
      Cell: ({ value }) => (
        <span className={`allocation-status ${value ? value.toLowerCase().replace('allocation', '') : 'none'}`}>
          {formatAllocationStatus(value)}
        </span>
      )
    },
    { 
      Header: 'Actions', 
      accessor: 'actions', 
      Cell: ({ row }) => (
        <RTButton
          type="link"
          onClick={() => navigate(`/paying-guest/view/${row.pg_id}`)}
          style={{ padding: '4px 8px', fontSize: '0.9rem' }}
        >
          View
        </RTButton>
      ) 
    }
  ];

return (
    <div className="rt-layout">
        <Menu className="rt-menu" />
        <div className="rt-main-content">
            <div className="rt-title-row">
                <h2 className="rt-title">Paying Guest List</h2>
                <div className="rt-actions">
                    <RTButton variant="secondary" onClick={() => navigate(-1)}>
                        &#8592; Back
                    </RTButton>
                </div>
            </div>
            <div className="rt-main-section">
                {/* Custom Filter Section */}
                <div className={`pg-filter-container ${isFilterCollapsed ? 'collapsed' : ''}`}>
                    <div 
                        className="pg-filter-header"
                        onClick={() => setIsFilterCollapsed(!isFilterCollapsed)}
                        style={{ cursor: 'pointer' }}
                    >
                        <div className="pg-filter-title">
                            <span className="filter-icon">🔍</span>
                            <span>Filter Paying Guests</span>
                            <span className={`pg-filter-chevron ${isFilterCollapsed ? 'collapsed' : 'expanded'}`}>
                                {isFilterCollapsed ? '▼' : '▲'}
                            </span>
                        </div>
                    </div>
                    {!isFilterCollapsed && (
                        <div className="pg-filter-content">
                        <div className="pg-filter-row">
                            <div className="pg-filter-group">
                                <label className="pg-filter-label">Search by Name</label>
                                <input
                                    type="text"
                                    className="pg-filter-input"
                                    placeholder="Enter name to search..."
                                    value={filters.name || ''}
                                    onChange={(e) => setFilters(prev => ({ ...prev, name: e.target.value }))}
                                />
                            </div>
                            <div className="pg-filter-group">
                                <label className="pg-filter-label">Search by Mobile</label>
                                <input
                                    type="text"
                                    className="pg-filter-input"
                                    placeholder="Enter mobile number..."
                                    value={filters.mobile || ''}
                                    onChange={(e) => setFilters(prev => ({ ...prev, mobile: e.target.value }))}
                                />
                            </div>
                            <div className="pg-filter-group">
                                <label className="pg-filter-label">Status</label>
                                <select
                                    className="pg-filter-select"
                                    value={filters.status || ''}
                                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value || undefined }))}
                                >
                                    <option value="">All</option>
                                    <option value="Active">Active</option>
                                    <option value="InActive">Inactive</option>
                                </select>
                            </div>
                        </div>
                        <div className="pg-filter-row">
                            <div className="pg-filter-checkbox-group">
                                {/* Space for future checkbox options */}
                            </div>
                            <div className="pg-filter-actions">
                                <RTButton
                                    variant="secondary"
                                    onClick={() => {
                                        setFilters({ status: 'Active' });
                                        setAppliedFilters({ status: 'Active' }); // Update applied filters
                                        fetchPGList({ status: 'Active' });
                                    }}
                                    className="pg-filter-clear-btn"
                                >
                                    Clear Filters
                                </RTButton>
                                <RTButton
                                    variant="primary"
                                    onClick={() => handleFilterApply(filters)}
                                    className="pg-filter-apply-btn"
                                >
                                    Apply Filters
                                </RTButton>
                            </div>
                        </div>
                    </div>
                    )}
                </div>

                {loading && <RTSpinner fullScreen={true} />}
                {error && <div className="alert alert-danger">{error}</div>}
                {!loading && !error && (
                    <div>
                        <RTTable
                            columns={columns}
                            data={pgList}
                            title={getTableTitle()}
                            showPagination={true}
                            showSearch={true}
                            exportable={true}
                            noDataText="No paying guests found."
                        />
                    </div>
                )}
            </div>
        </div>
    </div>
);
};

export default ViewPGList;